import { cn } from "@/lib/utils";

/**
 * Branded loading mark.
 *
 * ⛔ This used to render `@/assets/loading-animation.webp` with `dark:invert` —
 * BOTH of the patterns the brand-lockup fix retired (see
 * `src/__tests__/loading-screen-logo.test.ts`, which pins the pre-hydration
 * splash in index.html to the same rules):
 *
 *   - `loading-animation.webp` is the retired wordmark.
 *   - `filter: invert(1)` recoloured a black-on-transparent graphic for dark
 *     mode. Real light and dark variants exist now, so the hack must not come
 *     back.
 *
 * The marks live in `public/` rather than `src/assets/` because index.html is
 * static and parsed before Vite resolves modules — keeping both surfaces on the
 * same files means they cannot drift apart again.
 *
 * Variant naming follows the artwork's ink, not the theme it appears in:
 * `agileco-mark.svg` is dark ink for light backgrounds, `agileco-mark-light.svg`
 * is light ink for dark backgrounds.
 */

interface LoadingAnimationProps {
  size?: "sm" | "md" | "lg";
  text?: string;
  className?: string;
}

const sizeClasses = {
  sm: "w-24 h-auto",
  md: "w-40 h-auto",
  lg: "w-56 h-auto",
};

export function LoadingAnimation({
  size = "md",
  text,
  className,
}: LoadingAnimationProps) {
  return (
    <div
      className={cn("flex flex-col items-center justify-center gap-2", className)}
    >
      <img
        src="/agileco-mark.svg"
        alt="Loading"
        className={cn("block dark:hidden animate-pulse", sizeClasses[size])}
      />
      <img
        src="/agileco-mark-light.svg"
        alt=""
        aria-hidden="true"
        className={cn("hidden dark:block animate-pulse", sizeClasses[size])}
      />
      {text && <span className="text-sm text-muted-foreground">{text}</span>}
    </div>
  );
}
